<template>
  <div>
    <!-- Modal for image upload/capture -->
    <div class="modal fade" id="contactProfileImageModal" role="dialog" aria-labelledby="profileImageModalLabel"
         aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="profileImageModalLabel">Update Profile Image</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <!-- Method selection buttons (initial view) -->
            <div v-if="!activeMethod" class="text-center mb-4">
              <p class="text-muted mb-4">Choose how you want to update your profile image:</p>
              <div class="d-flex justify-content-center gap-3">
                <button class="btn btn-primary mx-2" @click="selectMethod('file')">
                  <i class="fas fa-upload mr-2"></i> Upload from File
                </button>
                <button class="btn btn-info mx-2" @click="selectMethod('webcam')">
                  <i class="fas fa-camera mr-2"></i> Use Webcam
                </button>
              </div>
            </div>

            <!-- File upload section -->
            <div v-if="activeMethod === 'file'" class="text-center">
              <div
                class="dropzone p-4 mb-3 border border-dashed rounded"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleFileDrop"
                :class="{'bg-light': isDragging}"
              >
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileUpload"
                  accept="image/*"
                  class="d-none"
                />

                <div v-if="!previewImage">
                  <i class="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
                  <p>Drag and drop an image here or</p>
                  <button class="btn btn-primary btn-sm" @click="$refs.fileInput.click()">
                    Browse Files
                  </button>
                </div>

                <img
                  v-if="previewImage"
                  :src="previewImage"
                  alt="Preview"
                  class="img-fluid mb-3"
                  style="max-height: 200px; border-radius: 5px;"
                />
              </div>

              <div v-if="previewImage" class="d-flex justify-content-center">
                <button class="btn btn-outline-secondary mx-2" @click="resetUpload">
                  <i class="fas fa-redo mr-1"></i> Choose Another
                </button>
                <button class="btn btn-success mx-2" @click="confirmImage">
                  <i class="fas fa-check mr-1"></i> Use This Image
                </button>
              </div>
            </div>

            <!-- Webcam capture section -->
            <div v-if="activeMethod === 'webcam'" class="text-center">
              <div v-if="!webcamActive && !capturedImage" class="mb-4">
                <i class="fas fa-camera fa-3x text-muted mb-3"></i>
                <p>Allow camera access to take a photo</p>
                <button class="btn btn-primary" @click="startWebcam">
                  <i class="fas fa-video mr-1"></i> Start Camera
                </button>
              </div>

              <div v-if="webcamActive && !capturedImage">
                <div class="position-relative mb-3">
                  <div class="form-group mb-2">
                    <select class="form-control" v-model="selectedDeviceId" @change="switchToSelectedCamera">
                      <option v-for="device in videoDevices" :key="device.deviceId" :value="device.deviceId">
                        {{ device.label || `Camera ${device.index + 1}` }}
                      </option>
                    </select>
                  </div>
                  <video
                    ref="webcam"
                    autoplay
                    class="w-100 border rounded"
                    style="max-height: 300px; background-color: #f8f9fa;"
                  ></video>
                  <div class="text-center mt-3">
                    <button class="btn btn-primary" @click="captureImage">
                      <i class="fas fa-camera mr-1"></i> Take Photo
                    </button>
                    <button class="btn btn-outline-secondary ml-2" @click="stopWebcam">
                      <i class="fas fa-times mr-1"></i> Cancel
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="capturedImage">
                <img
                  :src="capturedImage"
                  alt="Captured"
                  class="img-fluid mb-3"
                  style="max-height: 200px; border-radius: 5px;"
                />
                <div class="d-flex justify-content-center">
                  <button class="btn btn-outline-secondary mx-2" @click="retakePhoto">
                    <i class="fas fa-redo mr-1"></i> Retake Photo
                  </button>
                  <button class="btn btn-success mx-2" @click="confirmImage">
                    <i class="fas fa-check mr-1"></i> Use This Photo
                  </button>
                </div>
              </div>

              <canvas ref="canvas" style="display: none;"></canvas>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    <!-- Hidden link to trigger modal -->
    <!-- <a href="#" id="showProfileImageModal" data-toggle="modal" data-target="#profileImageModal" style="display: none;"></a> -->

    <!-- Hidden button to trigger modal -->
    <button
      id="showContactProfileImageModal"
      data-toggle="modal"
      data-target="#contactProfileImageModal"
      hidden="hidden"
    />
  </div>
</template>

<script>
export default {
  data() {
    return {
      activeMethod: null,
      isDragging: false,
      previewImage: null,
      webcamActive: false,
      capturedImage: null,
      stream: null,
      currentFacingMode: 'user', // 'user' is front camera, 'environment' is back camera
      videoDevices: [],
      selectedDeviceId: '',
    };
  },
  methods: {
    showModal() {
      // Reset state when opening modal
      this.resetState();

      // Open the modal using Bootstrap 3 pattern with a hidden link
      document.getElementById('showContactProfileImageModal').click();
    },

    closeModal() {
      // Clean up resources and reset state
      this.stopWebcam();
      this.resetState();

      // Using jQuery directly could cause issues if jQuery isn't loaded properly
      // So use the data-dismiss attribute approach instead
      document.querySelector('#contactProfileImageModal [data-dismiss="modal"]').click();
    },

    resetState() {
      this.activeMethod = null;
      this.isDragging = false;
      this.previewImage = null;
      this.capturedImage = null;
      this.webcamActive = false;
      this.selectedDeviceId = null;
    },

    selectMethod(method) {
      this.activeMethod = method;

      if (method === 'webcam') {
        // Get available cameras first, then start webcam
        this.getVideoDevices().then(() => {
          this.startWebcam();
        });
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.processSelectedFile(file);
      }
    },

    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.processSelectedFile(file);
      }
    },

    processSelectedFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    resetUpload() {
      this.previewImage = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async getVideoDevices() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.error("enumerateDevices() not supported.");
        return;
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.videoDevices = devices
          .filter(device => device.kind === 'videoinput')
          .map((device, index) => ({
            deviceId: device.deviceId,
            label: device.label || `Camera ${index + 1}`,
            index
          }));

        // If we have devices, select the first one by default
        if (this.videoDevices.length > 0) {
          this.selectedDeviceId = this.videoDevices[0].deviceId;
        }
      } catch (err) {
        console.error("Error enumerating devices:", err);
      }
    },

    startWebcam(deviceId = this.selectedDeviceId) {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Stop any existing stream first
        this.stopWebcam();

        const constraints = {
          video: deviceId ? {deviceId: deviceId} : true
        };

        // If no specific device is selected but we want to use front/back
        if (!deviceId && this.currentFacingMode) {
          constraints.video = {facingMode: this.currentFacingMode};
        }

        navigator.mediaDevices.getUserMedia(constraints)
          .catch((error) => {
            if (error.name === 'OverconstrainedError') {
              // Try again with simpler constraints
              return navigator.mediaDevices.getUserMedia({video: true});
            }
            throw error;
          })
          .then((stream) => {
            this.stream = stream;
            this.webcamActive = true;
            this.$nextTick(() => {
              if (this.$refs.webcam) {
                this.$refs.webcam.srcObject = stream;
              }
            });

            // Get devices if we haven't already (this will also get labels after permission)
            if (this.videoDevices.length === 0) {
              this.getVideoDevices();
            }
          })
          .catch((error) => {
            console.error("Error accessing webcam: ", error);
            alert('Could not access your camera. Please check permissions and try again.');
          });
      } else {
        alert('Your browser does not support webcam access.');
      }
    },

    stopWebcam() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      this.webcamActive = false;
    },

    captureImage() {
      const canvas = this.$refs.canvas;
      const video = this.$refs.webcam;

      if (canvas && video) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);

        this.capturedImage = canvas.toDataURL('image/png');
        this.stopWebcam();
      }
    },

    retakePhoto() {
      this.capturedImage = null;
      this.startWebcam();
    },

    confirmImage() {
      console.log(this.capturedImage)
      console.log(this.capturedImage)
      const imageData = this.previewImage || this.capturedImage;
      if (imageData) {
        this.$emit('contact-profile-image-selected', imageData);
        this.closeModal();
      }
    },

    switchToSelectedCamera() {
      if (this.selectedDeviceId) {
        this.startWebcam(this.selectedDeviceId);
      }
    },
  },
  beforeDestroy() {
    this.stopWebcam();
  },
  mounted() {
    this.getVideoDevices();
  }
};
</script>

<style scoped>
.border-dashed {
  border-style: dashed !important;
}

.dropzone {
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover {
  background-color: #f8f9fa;
}
</style>



// WEBPACK FOOTER //
// src/components/ContactDetails/ContactImageUploader.vue